import type { OfflineSceneKey, OfflineViewState } from "@novorender/api";
import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { selectIncrementalCachingDisabled, selectIsOnline } from "slices/explorer";

import type { FullSyncController } from "./fullSyncController";
import { recordFromState } from "./offlineRecord";
import { offlineActions, selectOfflineScenes } from "./offlineSlice";
import { trackOfflineFailure } from "./offlineTelemetry";
import { useGetOfflineSceneName } from "./useOfflineSceneName";

/**
 * Names the open scene's record when it is incremental, so the list can tell it from the others;
 * a sync records its own name. The SDK names only a directory that already reads incremental, so a
 * scene enrolled this session is named on a later load. Tried once per scene directory a session,
 * once a name can be had (on Procore, online). Not under the iOS kill switch, which leaves what is
 * cached as it is.
 * @returns The name write under way, if any. It holds the scene's sync lock for a moment, so a
 *   sync or delete of the scene waits for it rather than fail busy. Never rejects.
 */
export function useNameIncrementalScene({
    offlineState,
    sceneKey,
    sceneDirName,
    isProjectScene,
    fullSyncs,
}: {
    offlineState: OfflineViewState | undefined;
    sceneKey: OfflineSceneKey;
    sceneDirName: string;
    isProjectScene: boolean | undefined;
    fullSyncs: FullSyncController;
}): () => Promise<void> | undefined {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const getOfflineSceneName = useGetOfflineSceneName();
    const incrementalCachingDisabled = useAppSelector(selectIncrementalCachingDisabled);
    const isOnline = useAppSelector(selectIsOnline);
    const status = useAppSelector((state) => selectOfflineScenes(state)[sceneDirName]?.view.kind);
    const named = useRef<string>();
    const nameWrite = useRef<Promise<void>>();

    useEffect(() => {
        if (
            !offlineState ||
            !isProjectScene ||
            status !== "incremental" ||
            incrementalCachingDisabled ||
            named.current === sceneDirName
        ) {
            return;
        }
        let stopped = false;
        (async () => {
            const name = await getOfflineSceneName();
            const record = store.getState().offline.scenes[sceneDirName];
            if (stopped || name === undefined || record?.view.kind !== "incremental") {
                return;
            }
            named.current = sceneDirName;
            // A full sync starting here takes the scene over, and records its own name.
            if (record.name === name || fullSyncs.isRunning || fullSyncs.hasUnsettledSync) {
                return;
            }
            const writing = (async () => {
                const scene = await offlineState.scene(sceneKey, { name });
                const state = await scene.getState();
                // Stored even if the effect has run again since: the write it started is done.
                const shown = store.getState().offline.scenes[sceneDirName];
                if (state.kind === "incremental" && !fullSyncs.hasUnsettledSync && shown?.view.kind === "incremental") {
                    const named = recordFromState(scene.id, state, false, shown);
                    if (named) {
                        dispatch(offlineActions.addScene(named));
                    }
                }
            })();
            nameWrite.current = writing.catch(() => {});
            await writing;
        })().catch((e) => {
            // The scene is listed by its id until a later load names it.
            void trackOfflineFailure({
                error: e,
                source: "offline.useHandleOffline.nameIncremental",
                scene: offlineState.getScene(sceneKey),
            });
        });
        return () => {
            stopped = true;
        };
    }, [
        offlineState,
        isProjectScene,
        status,
        incrementalCachingDisabled,
        isOnline,
        sceneKey,
        sceneDirName,
        getOfflineSceneName,
        fullSyncs,
        store,
        dispatch,
    ]);

    return useCallback(() => nameWrite.current, []);
}
